import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton } from '@/components/data';
import api from '@/lib/api';
import { downloadAuthedFile } from '@/lib/fileDownload';
import { formatApiError } from '@/lib/validators';
import { formatIST, formatRelativeIST } from '@/lib/utils';
import {
  Plus, Search, RefreshCw, Download, Copy, Trash2, Pencil, Receipt, Users,
} from 'lucide-react';

import { STATUSES, STATUS_TONE, PROFORMA_INVOICE } from './documentTypes';
import { money, rupees } from './totals';
import CustomerGroups from './CustomerGroups';

/**
 * Saved quotations, or saved proforma invoices — one list for both.
 *
 * Money documents, so the row carries the grand total and who raised it, and
 * the status is changed in the row itself rather than behind an edit screen:
 * "did they accept it" is the only thing most of these rows are ever revisited
 * for, and making that a navigation is what leaves a list of quotations sitting
 * at draft forever.
 */

const PAGE_SIZE = 50;

// DataGrid sorts by column key, the API by sort_by — these four are the columns
// whose keys are named to be the same word on both sides.
const SORTABLE = ['number', 'date', 'customer', 'amount'];

export default function SalesDocumentList({ docType }) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [status, setStatus] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [grouped, setGrouped] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setPage(1); }, [searchDebounce, status, customerId, dateFrom, dateTo]);

  const filters = useMemo(() => {
    const params = {};
    if (searchDebounce) params.search = searchDebounce;
    if (status) params.status = status;
    if (customerId) params.customer_id = customerId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [searchDebounce, status, customerId, dateFrom, dateTo]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      if (grouped) {
        const res = await api.get(`${docType.api}/grouped-by-customer`, { params: filters });
        setGroups(res.data.groups || []);
      } else {
        const res = await api.get(docType.api, {
          params: {
            ...filters,
            page,
            limit: PAGE_SIZE,
            sort_by: sort?.key || 'date',
            sort_order: sort?.dir || 'desc',
          },
        });
        setDocuments(res.data.documents || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      toast.error(formatApiError(err, `Failed to load ${docType.listTitle.toLowerCase()}`));
    }
    setLoading(false);
  }, [docType, filters, grouped, page, sort]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  useEffect(() => {
    api.get('/customers', { params: { limit: 200 } })
      .then((res) => setCustomers(res.data.customers || []))
      .catch(() => setCustomers([]));
  }, []);

  const openDocument = useCallback((row) => navigate(`${docType.route}/${row.id}`), [docType, navigate]);

  const changeStatus = useCallback(async (row, next) => {
    try {
      await api.patch(`${docType.api}/${row.id}/status`, { status: next });
      toast.success(`${row.number} is ${next}`);
      fetchDocuments();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to change the status'));
    }
  }, [docType, fetchDocuments]);

  const duplicate = useCallback(async (row) => {
    try {
      const res = await api.post(`${docType.api}/${row.id}/duplicate`);
      toast.success(`Copied ${row.number} to ${res.data.number}`);
      navigate(`${docType.route}/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to duplicate'));
    }
  }, [docType, navigate]);

  const convert = useCallback(async (row) => {
    try {
      const res = await api.post(`${docType.api}/${row.id}/convert-to-proforma`);
      toast.success(`${row.number} became ${res.data.number}`);
      navigate(`${PROFORMA_INVOICE.route}/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to convert this quotation'));
    }
  }, [docType.api, navigate]);

  const download = useCallback(async (row) => {
    try {
      await downloadAuthedFile(`${docType.api}/${row.id}/pdf`, `${row.number}.pdf`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to download the PDF'));
    }
  }, [docType]);

  const remove = useCallback(async () => {
    const row = confirmDelete;
    setConfirmDelete(null);
    if (!row) return;
    try {
      await api.delete(`${docType.api}/${row.id}`);
      toast.success(`${row.number} deleted`);
      fetchDocuments();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete'));
    }
  }, [confirmDelete, docType, fetchDocuments]);

  const columns = useMemo(() => ([
    {
      key: 'number',
      header: docType.numberLabel,
      width: '10rem',
      sortable: true,
      render: (row) => <span className="block truncate font-mono text-[12px] font-semibold text-doc-navy">{row.number}</span>,
    },
    {
      key: 'date',
      header: 'Date',
      width: '6.5rem',
      sortable: true,
      render: (row) => <span className="text-[12px] text-slate-600">{formatIST(row.date, { dateOnly: true })}</span>,
    },
    {
      key: 'customer',
      header: 'Customer',
      width: 'minmax(10rem,1.3fr)',
      sortable: true,
      render: (row) => (
        <>
          <span className="block truncate text-slate-700">{row.customer_name || '—'}</span>
          {row.bill_to?.name && row.bill_to.name !== row.customer_name && (
            <span className="block truncate text-[11px] leading-tight text-slate-400">{row.bill_to.name}</span>
          )}
        </>
      ),
    },
    {
      key: 'product',
      header: 'Label / product',
      width: 'minmax(9rem,1fr)',
      mobile: false,
      render: (row) => <span className="block truncate text-[12px] text-slate-600">{row.specs?.product_name || '—'}</span>,
    },
    {
      key: 'amount',
      header: 'Grand total',
      width: '8.5rem',
      align: 'right',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-800">{rupees(row.totals?.grand_total)}</span>,
      total: (rows) => `₹ ${money(rows.reduce((sum, row) => sum + (row.totals?.grand_total || 0), 0))}`,
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      render: (row) => (
        // stopPropagation: the row itself opens the document, and changing the
        // status must not also navigate away from the list you are working down
        <span onClick={(e) => e.stopPropagation()}>
          <select
            value={row.status}
            onChange={(e) => changeStatus(row, e.target.value)}
            aria-label={`Status of ${row.number}`}
            data-testid={`status-${row.id}`}
            className={`w-full cursor-pointer bg-transparent px-1 py-0.5 text-[12px] font-medium focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-blue ${STATUS_TONE[row.status] || 'text-slate-500'}`}
          >
            {STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </span>
      ),
    },
    {
      key: 'valid_until',
      header: 'Valid until',
      width: '6.5rem',
      mobile: false,
      render: (row) => (
        <span className="text-[12px] text-slate-500">
          {row.valid_until ? formatIST(row.valid_until, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'created_by_name',
      header: 'Raised by',
      width: '8rem',
      mobile: false,
      render: (row) => <span className="block truncate text-[11px] text-slate-500">{row.created_by_name || '—'}</span>,
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '7rem',
      mobile: false,
      render: (row) => (
        <span className="text-[11px] text-slate-500" title={formatIST(row.created_at)}>
          {formatRelativeIST(row.created_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: docType.hasReference ? '9.5rem' : '8rem',
      align: 'right',
      render: (row) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton icon={Pencil} label={`Open ${row.number}`} iconOnly
            onClick={() => openDocument(row)} testId={`open-${row.id}`} />
          <CellButton icon={Download} label={`Download ${row.number} as PDF`} iconOnly
            onClick={() => download(row)} testId={`pdf-${row.id}`} />
          <CellButton icon={Copy} label={`Duplicate ${row.number}`} iconOnly
            onClick={() => duplicate(row)} testId={`duplicate-${row.id}`} />
          {docType.hasReference && (
            <CellButton icon={Receipt} label={`Convert ${row.number} to a proforma invoice`} iconOnly tone="accent"
              onClick={() => convert(row)} testId={`convert-${row.id}`} />
          )}
          <CellButton icon={Trash2} label={`Delete ${row.number}`} iconOnly tone="danger"
            onClick={() => setConfirmDelete(row)} testId={`delete-${row.id}`} />
        </span>
      ),
    },
  ]), [changeStatus, convert, docType, download, duplicate, openDocument]);

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const pageValue = documents.reduce((sum, row) => sum + (row.totals?.grand_total || 0), 0);

  return (
    <Screen data-testid={`${docType.key}-list-page`}>
      <CommandBar
        title={docType.listTitle}
        subtitle={grouped ? `${groups.length} customers` : `${total} on file`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchDocuments, disabled: loading },
          {
            key: 'new', label: `New ${docType.docTitle.toLowerCase()}`, icon: Plus, tone: 'accent', mnemonic: 'n',
            onClick: () => navigate(`${docType.route}/new`), testId: 'new-document-btn',
          },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-9 pl-8 text-sm sm:h-7 sm:text-xs"
            placeholder="Number, customer, product, item…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="document-search"
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: null, label: grouped ? 'customers' : 'documents', value: grouped ? groups.length : total },
          !grouped && { key: null, label: 'value on this page', value: `₹ ${money(pageValue)}`, tone: 'blue' },
        ].filter(Boolean)}
      >
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Filter by status"
          data-testid="status-filter"
          className="h-9 rounded border border-slate-200 bg-white px-2 text-sm text-slate-700 sm:h-7 sm:text-xs"
        >
          <option value="">Any status</option>
          {STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          aria-label="Filter by customer"
          data-testid="customer-filter"
          className="h-9 max-w-[10rem] rounded border border-slate-200 bg-white px-2 text-sm text-slate-700 sm:h-7 sm:text-xs"
        >
          <option value="">All customers</option>
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>{customer.customer_name}</option>
          ))}
        </select>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          aria-label="From date"
          data-testid="date-from"
          className="h-9 w-32 text-sm sm:h-7 sm:text-xs"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          aria-label="To date"
          data-testid="date-to"
          className="h-9 w-32 text-sm sm:h-7 sm:text-xs"
        />
        <button
          type="button"
          onClick={() => setGrouped((on) => !on)}
          aria-pressed={grouped}
          data-testid="group-by-customer-toggle"
          className={`flex h-9 items-center gap-1.5 rounded border px-2 text-sm transition-colors sm:h-7 sm:text-xs ${
            grouped
              ? 'border-brand-blue bg-brand-blue/10 text-brand-blue'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Users className="h-3.5 w-3.5" /> Group by customer
        </button>
      </StatStrip>

      <Pane
        title={grouped ? 'By customer' : docType.listTitle}
        aside={grouped ? `${groups.length} shown` : `${documents.length} shown`}
        scroll={grouped}
        bodyClassName={grouped ? undefined : 'flex flex-col'}
        actions={!grouped && totalPages > 1 && (
          <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              data-testid="prev-page-btn"
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
            {page} / {totalPages}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              data-testid="next-page-btn"
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
          </span>
        )}
      >
        {grouped ? (
          <CustomerGroups groups={groups} loading={loading} onOpen={openDocument} />
        ) : (
          <DataGrid
            data-testid="documents-table"
            columns={columns}
            rows={documents}
            loading={loading}
            getRowId={(row) => row.id}
            onRowClick={openDocument}
            sort={sort}
            onSortChange={(next) => setSort(next && SORTABLE.includes(next.key) ? next : { key: 'date', dir: 'desc' })}
            showTotals={documents.length > 0}
            empty={
              <span className="flex flex-col items-center gap-2 text-slate-400">
                <docType.icon className="h-8 w-8 text-slate-300" />
                {docType.emptyList}
              </span>
            }
          />
        )}
      </Pane>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.number}?</AlertDialogTitle>
            <AlertDialogDescription>
              It leaves this list and the customer's history. An admin can still find it in the recycle bin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Screen>
  );
}
